import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TradeCategory } from './TradeCategories';
import { useDebounce } from '@/hooks/use-debounce';
import { searchTradeByContact } from '@/lib/supabase/trades';
import { PotentialTradeMatch } from './PotentialTradeMatch';
import { UserTradesperson, Tradesperson } from '@/lib/types';
import { debug } from '@/lib/utils';

export const tradeFormSchema = z.object({
  phone: z.string().optional(),
  email: z.string().email().optional(),
  organization: z.string().optional(),
  contact: z.string().optional(),
  notes: z.string().optional(),
}).refine(
  (data) => (data.phone && data.phone.trim() !== '') || 
            (data.email && data.email.trim() !== ''),
  { 
    message: "Either phone number or email address is required", 
    path: ["phone"] 
  }
);

export type TradeFormData = z.infer<typeof tradeFormSchema>;

interface TradeFormProps {
  category: TradeCategory;
  onBack: () => void;
  onSubmit: (data: TradeFormData) => void;
}

export function TradeForm({ category, onBack, onSubmit }: TradeFormProps) {
  const [potentialMatch, setPotentialMatch] = useState<{
    trade: UserTradesperson;
    matchType: "phone" | "email";
    matchValue: string;
  } | null>(null);
  
  const form = useForm<TradeFormData>({
    resolver: zodResolver(tradeFormSchema),
    defaultValues: {
      phone: '',
      email: '',
      organization: '',
      contact: '',
      notes: '',
    }
  });

  const phone = form.watch("phone");
  const email = form.watch("email");
  
  const debouncedPhone = useDebounce(phone, 300);
  const debouncedEmail = useDebounce(email, 300);
  
  useEffect(() => {
    async function checkForMatch() {
      if (!debouncedPhone && !debouncedEmail) {
        setPotentialMatch(null);
        return;
      }
      
      try {
        const match = await searchTradeByContact(
          debouncedPhone,
          debouncedEmail,
          category
        );
        
        if (match) {
          const tradesperson: Tradesperson = match;
          setPotentialMatch({
            trade: {
              id: tradesperson.id,
              tradesperson: tradesperson,
              user_id: '',
              tradesperson_id: tradesperson.id,
              preferred: false,
              created_at: new Date().toISOString(),
              notes: '',
            },
            matchType: debouncedPhone ? "phone" : "email",
            matchValue: debouncedPhone || debouncedEmail || "",
          });
        } else {
          setPotentialMatch(null);
        }
      } catch (error) {
        debug.error("Error searching for matches:", error);
      }
    }
    
    checkForMatch();
  }, [debouncedPhone, debouncedEmail, category]);

  const handleSubmit = (data: TradeFormData) => {
    onSubmit(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        {potentialMatch && (
          <PotentialTradeMatch
            trade={potentialMatch.trade}
            matchType={potentialMatch.matchType}
            matchValue={potentialMatch.matchValue}
            onUseExisting={(trade) => {
              // Handle using existing trade
              if (trade.tradesperson) {
                onSubmit({
                  phone: trade.tradesperson.phone || '',
                  email: trade.tradesperson.email || '',
                  organization: trade.tradesperson.org || '',
                  contact: trade.tradesperson.contact || '',
                  notes: trade.notes || '',
                });
              }
            }}
            onDismiss={() => setPotentialMatch(null)}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="Phone number" {...field} />
                </FormControl>
                <FormDescription>Required if no email provided</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Email address" {...field} />
                </FormControl>
                <FormDescription>Required if no phone provided</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organization"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization</FormLabel>
                <FormControl>
                  <Input placeholder="Organization name" {...field} />
                </FormControl>
                <FormDescription>Optional</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="contact"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Name</FormLabel>
                <FormControl>
                  <Input placeholder="Contact person" {...field} />
                </FormControl>
                <FormDescription>Optional</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Input placeholder="Additional notes" {...field} />
              </FormControl>
              <FormDescription>Optional</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button type="submit">Add Trade</Button>
        </div>
      </form>
    </Form>
  );
}